<template>
	<div :class="containerClass" @click="onWrapperClick">
		<FrameTopbar @menu-toggle="onMenuToggle" />

        <transition name="layout-sidebar">
            <div :class="sidebarClass" @click="onSidebarClick" v-show="isSidebarVisible()">
                <div class="layout-logo">
                    <img alt="Logo" :src="logo" style="width: 80%" />
                </div>

                <FrameProfile />
                <FrameMenu :model="menu" @menuitem-click="onMenuItemClick" />
            </div>
        </transition>

		<div class="layout-main">
			<router-view />
		</div>

		<FrameFooter />
	</div>
</template>

<script>
import FrameTopbar from './FrameTopbar.vue';
import FrameProfile from './FrameProfile.vue';
import FrameMenu from './FrameMenu.vue';
import FrameFooter from './FrameFooter.vue';
import urljoin from "url-join";
import jwtDecode from "jwt-decode";
import {JwtToken} from "@/utils/jwt";

export default {
    data() {
        return {
            layoutMode: 'static',
            layoutColorMode: 'dark',
            staticMenuInactive: false,
            overlayMenuActive: false,
            mobileMenuActive: false
        }
    },
    watch: {
        $route() {
            this.menuActive = false;
            this.$toast.removeAllGroups();
        }
    },
    methods: {
        onWrapperClick() {
            if (!this.menuClick) {
                this.overlayMenuActive = false;
                this.mobileMenuActive = false;
            }

            this.menuClick = false;
        },
        onMenuToggle() {
            this.menuClick = true;

            if (this.isDesktop()) {
                if (this.layoutMode === 'overlay') {
                    this.overlayMenuActive = !this.overlayMenuActive;
                }
                else if (this.layoutMode === 'static') {
                    this.staticMenuInactive = !this.staticMenuInactive;
                }
            }
            else {
                this.mobileMenuActive = !this.mobileMenuActive;
            }

            event.preventDefault();
        },
        onSidebarClick() {
            this.menuClick = true;
        },
        onMenuItemClick(event) {
            if (event.item && !event.item.items) {
                this.overlayMenuActive = false;
                this.mobileMenuActive = false;
            }
        },
        addClass(element, className) {
            if (element.classList)
                element.classList.add(className);
            else
                element.className += ' ' + className;
        },
        removeClass(element, className) {
            if (element.classList)
                element.classList.remove(className);
            else
                element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        },
        isDesktop() {
            return window.innerWidth > 1024;
        },
        isSidebarVisible() {
            if (this.isDesktop()) {
                if (this.layoutMode === 'static')
                    return !this.staticMenuInactive;
                else if (this.layoutMode === 'overlay')
                    return this.overlayMenuActive;
                else
                    return true;
            }
            else {
                return true;
            }
        },
        redirectToSSO() {
            let continueUrl = urljoin(
                "/sso",
                `?refresh_token=${localStorage.getItem('refresh_token')}`,
                `?access_token=${localStorage.getItem('access_token')}`
            );

            if (JwtToken.fromLocalStorage().isProfileSwitcher()) {
                continueUrl = urljoin('/view_for', `?continue=${encodeURIComponent(continueUrl)}`);
            }

            return continueUrl;
        }
    },
    computed: {
        menu() {
            const items = [
                {label: this.$t('performanceDashboard'), icon: 'pi pi-fw pi-chart-bar', to: '/dashboard'},
                {label: this.$t('chemicals'), icon: 'pi pi-fw pi-info', items: [
                    {label: this.$t('bulkImport'), icon: 'pi pi-file', to: '/chemical/bulk-import'}
                ]},
                {label: this.$t('organization'), icon: 'pi pi-fw pi-users', to: '/organization'},
                {label: 'mnavigator', icon: 'pi pi-fw pi-sign-in', command: () => {window.location = this.redirectToSSO()}}
            ];

            if (JwtToken.fromLocalStorage().isSystemAdmin()) {
                items[1].items.push(
                    {label: this.$t('bulkImportGlobalStatus'), icon: 'pi pi-file', to: '/chemical/bulk-import-global-status'}
                );

                items.splice(2, 0,
                    { label: '(demo) Dashboard', icon: 'pi pi-fw pi-home', to: '/demo/dashboard'},
                    {
                        label: '(demo) Components', icon: 'pi pi-fw pi-globe', badge: '9',
                        items: [
                            {label: 'Sample Page', icon: 'pi pi-fw pi-th-large', to: '/demo/sample'},
                            {label: 'Forms', icon: 'pi pi-fw pi-file', to: '/demo/forms'},
                            {label: 'Data', icon: 'pi pi-fw pi-table', to: '/demo/data'},
                            {label: 'Panels', icon: 'pi pi-fw pi-list', to: '/demo/panels'},
                            {label: 'Overlays', icon: 'pi pi-fw pi-clone', to: '/demo/overlays'},
                            {label: 'Menus', icon: 'pi pi-fw pi-plus', to: '/demo/menus'},
                            {label: 'Messages', icon: 'pi pi-fw pi-spinner', to: '/demo/messages'},
                            {label: 'Charts', icon: 'pi pi-fw pi-chart-bar', to: '/demo/charts'},
                            {label: 'Misc', icon: 'pi pi-fw pi-upload', to: '/demo/misc'}
                        ]
                    },
                    {
                        label: '(demo) Template Pages', icon: 'pi pi-fw pi-file',
                        items: [
                            {label: 'Empty Page', icon: 'pi pi-fw pi-circle-off', to: '/demo/empty'}
                        ]
                    },
                    {
                        label: '(demo) Menu Hierarchy', icon: 'pi pi-fw pi-search',
                        items: [
                            {
                                label: 'Submenu 1', icon: 'pi pi-fw pi-bookmark',
                                items: [
                                    {
                                        label: 'Submenu 1.1', icon: 'pi pi-fw pi-bookmark',
                                        items: [
                                            {label: 'Submenu 1.1.1', icon: 'pi pi-fw pi-bookmark'},
                                            {label: 'Submenu 1.1.2', icon: 'pi pi-fw pi-bookmark'},
                                            {label: 'Submenu 1.1.3', icon: 'pi pi-fw pi-bookmark'},
                                        ]
                                    },
                                    {
                                        label: 'Submenu 1.2', icon: 'pi pi-fw pi-bookmark',
                                        items: [
                                            {label: 'Submenu 1.2.1', icon: 'pi pi-fw pi-bookmark'},
                                            {label: 'Submenu 1.2.2', icon: 'pi pi-fw pi-bookmark'}
                                        ]
                                    },
                                ]
                            },
                            {
                                label: 'Submenu 2', icon: 'pi pi-fw pi-bookmark',
                                items: [
                                    {
                                        label: 'Submenu 2.1', icon: 'pi pi-fw pi-bookmark',
                                        items: [
                                            {label: 'Submenu 2.1.1', icon: 'pi pi-fw pi-bookmark'},
                                            {label: 'Submenu 2.1.2', icon: 'pi pi-fw pi-bookmark'},
                                            {label: 'Submenu 2.1.3', icon: 'pi pi-fw pi-bookmark'},
                                        ]
                                    },
                                    {
                                        label: 'Submenu 2.2', icon: 'pi pi-fw pi-bookmark',
                                        items: [
                                            {label: 'Submenu 2.2.1', icon: 'pi pi-fw pi-bookmark'},
                                            {label: 'Submenu 2.2.2', icon: 'pi pi-fw pi-bookmark'}
                                        ]
                                    }
                                ]
                            }
                        ],
                    });
            }

            return items;
        },
        tokenDecoded() {
            return jwtDecode(localStorage.getItem('access_token'));
        },
        containerClass() {
            return ['layout-wrapper', {
                'layout-overlay': this.layoutMode === 'overlay',
                'layout-static': this.layoutMode === 'static',
                'layout-static-sidebar-inactive': this.staticMenuInactive && this.layoutMode === 'static',
                'layout-overlay-sidebar-active': this.overlayMenuActive && this.layoutMode === 'overlay',
                'layout-mobile-sidebar-active': this.mobileMenuActive
            }];
        },
        sidebarClass() {
            return ['layout-sidebar', {
                'layout-sidebar-dark': this.layoutColorMode === 'dark',
                'layout-sidebar-light': this.layoutColorMode === 'light'
            }];
        },
        logo() {
            return (this.layoutColorMode === 'dark') ? "/assets/images/logo.png" : "/assets/layout/images/logo.svg";
        }
    },
    beforeUpdate() {
        if (this.mobileMenuActive)
            this.addClass(document.body, 'body-overflow-hidden');
        else
            this.removeClass(document.body, 'body-overflow-hidden');
    },
    components: {
        'FrameTopbar': FrameTopbar,
        'FrameProfile': FrameProfile,
        'FrameMenu': FrameMenu,
        'FrameFooter': FrameFooter,
    }
}
</script>

<style lang="scss">
    .layout-main {
        margin-bottom: 50px;
    }
</style>
